import type React from "react";
import { useState, useEffect, useCallback } from "react";
import {
  Autocomplete,
  CircularProgress,
  Box,
  Typography,
  Stack,
} from "@mui/material";
import { Booking } from "../types/booking.types";
import apiService from "../../../service/Api";
import CustomTextField from "../../../components/common/Textfield";

function useDebounce<T>(value: T, delay = 500): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const h = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(h);
  }, [value, delay]);
  return debounced;
}

interface DeceasedSearchProps {
  onBookingSelect: (booking: Booking | null) => void;
}

const DeceasedSearch: React.FC<DeceasedSearchProps> = ({ onBookingSelect }) => {
  const [inputValue, setInputValue] = useState("");
  const [options, setOptions] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);

  const debounced = useDebounce(inputValue, 400);

  const fetchBookings = useCallback(async (search: string) => {
    if (!search || search.length < 3) {
      setOptions([]);
      return;
    }

    setLoading(true);
    try {
      const resp = await apiService.get<{ results: Booking[] }>(
        "/api/bookings/booking/",
        {
          search,
          page_size: 10,
        }
      );
      setOptions(resp.data.results || []);
    } catch (err) {
      console.error("Error fetching bookings:", err);
      setOptions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBookings(debounced);
  }, [debounced, fetchBookings]);

  return (
    <Box>
      <Autocomplete
        options={options}
        getOptionLabel={(opt) => {
          const dec = opt.deceased?.[0];
          const event = opt.events?.[0];

          if (dec) {
            return `${dec.first_name} ${dec.surname}${
              event ? ` - ${event.title}` : ""
            }`;
          }
          if (event) return event.title;

          return "Unknown Booking";
        }}
        value={selectedBooking}
        onChange={(_, newVal) => {
          setSelectedBooking(newVal);
          onBookingSelect(newVal || null);
        }}
        inputValue={inputValue}
        onInputChange={(_, newInput) => setInputValue(newInput)}
        loading={loading}
        renderInput={(params) => (
          <CustomTextField
            {...params}
            placeholder="Search deceased name or event title..."
            variant="outlined"
            helperText="Type at least 3 letters"
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress size={18} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        renderOption={(props, option) => {
          const dec = option.deceased?.[0];
          const event = option.events?.[0];
          return (
            <li {...props} key={option.id}>
              <Stack direction="column">
                <Typography variant="body1" fontWeight={500}>
                  {dec
                    ? `${dec.first_name} ${dec.surname}`
                    : event?.title || "Unknown Booking"}
                </Typography>
                {dec && event && (
                  <Typography variant="body2" color="text.secondary">
                    {event.title}
                  </Typography>
                )}
              </Stack>
            </li>
          );
        }}
        sx={{ width: "100%" }}
      />
    </Box>
  );
};

export default DeceasedSearch;
